import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';
import { Consent } from "@/types/openpayments/consent";
import { AccountType, BankAccountSummary, ConnectAccountResult, RelinkAccountsResult, WaitingSettlementsSummary } from "@/types/accounts";
import { codedError, extractFunctionError } from "@/utils/errorUtils";
import { handleError } from "@/utils/handleError";

// caller-access-context.EC5/AC3 (issue #616) — src/ is a real browser session with no BFF hop
// in front of it, so extractCallerContext's real-IP/UA fail-loud check is satisfied by the
// browser's own connection; this header just declares the discriminated-union accessMode
// explicitly rather than relying on the header's absence to mean the same thing.
const CALLER_ACCESS_MODE_HEADERS = { "x-caller-access-mode": "user-present" } as const;

/** A designed mapping refusal (FIN-90 PR4, FIN-968), re-thrown with its error code and details
 * intact so the cell component can branch on it instead of a generic toast. */
const CHANGE_MAPPING_OUTCOMES = [
  'CHART_MAPPING_LOCKED',
  'CHART_ACCOUNT_UNAVAILABLE',
  'CHART_ACCOUNT_HAS_HISTORY',
  'CHART_ACCOUNT_OUT_OF_RANGE',
  'CHART_MAPPING_PRIMARY_SWAP_ONLY',
] as const;
const SWAP_MAPPING_OUTCOMES = ['CHART_ACCOUNT_UNAVAILABLE', 'CHART_ACCOUNT_OUT_OF_RANGE', 'CHART_MAPPING_CHANGED'] as const;

type MappingActionErrorCode = (typeof CHANGE_MAPPING_OUTCOMES)[number] | (typeof SWAP_MAPPING_OUTCOMES)[number];

interface MappingActionError extends Error {
  error: MappingActionErrorCode;
  details?: unknown;
}

/** Re-throws `error` as a MappingActionError when it carries one of `codes`; otherwise returns. */
async function rethrowDesignedMappingOutcome(error: unknown, codes: readonly MappingActionErrorCode[]): Promise<MaybeCoded> {
  const functionError: MaybeCoded = await extractFunctionError(error);
  // The code arrives either on the parsed FunctionsHttpError body or on the returned error itself.
  const body = functionError ?? (error as MaybeCoded);
  if (body?.error && (codes as readonly string[]).includes(body.error)) {
    const coded = new Error(body.message ?? body.error) as MappingActionError;
    coded.error = body.error as MappingActionErrorCode;
    coded.details = body.details;
    throw coded;
  }
  return functionError;
}

/** Shape of the structured error an edge function returns in `error`/`message`. */
type MaybeCoded = { error?: string; message?: string; details?: unknown } | null | undefined;

const isConsentOrKyc = (code: string | undefined, includeKyc: boolean): boolean =>
  code === 'CONSENT_REQUIRED' || (includeKyc && code === 'KYC_REQUIRED');

/**
 * Every action below shares this same pre-catch branch: a structured CONSENT_REQUIRED must
 * surface as its own coded Error (which the UI branches on, FIN-381) rather than the raw
 * transport error, and anything else rethrows untouched.
 */
function throwIfCoded(error: MaybeCoded): void {
  if (!error) return;
  if (error.error === 'CONSENT_REQUIRED') throw codedError('CONSENT_REQUIRED', error.details);
  throw error;
}

/**
 * The catch-block half of the same contract: re-throw a coded failure without a toast, whether
 * the code arrived in the parsed function body or already on the caught error itself. Anything
 * else falls through to the caller's own handleError().
 */
function rethrowIfConsentOrKycRequired(
  error: MaybeCoded,
  functionError: MaybeCoded,
  { includeKyc = false }: { includeKyc?: boolean } = {},
): void {
  if (isConsentOrKyc(functionError?.error, includeKyc)) {
    throw codedError(functionError!.error!, functionError!.details);
  }
  if (isConsentOrKyc(error?.message, includeKyc) || isConsentOrKyc(error?.error, includeKyc)) {
    throw error;
  }
}

/**
 * An account-api action addressed by bank identity alone. CONSENT_REQUIRED/KYC_REQUIRED are
 * re-thrown for the caller's own flow; anything else is reported via handleError and re-thrown.
 */
/** ref/countryCode identify a bic-less bank, whose bic is the empty string (FIN-783); accountType
 *  is FIN-1567's. Shared so the two action bodies that send all three never drift apart. */
function bankIdentityFields(ref?: string, countryCode?: string, accountType?: AccountType) {
  return {
    ...(ref ? { ref } : {}),
    ...(countryCode ? { countryCode } : {}),
    ...(accountType ? { accountType } : {}),
  };
}

async function invokeBankIdentityAction<T>(
  action: 'getBankAccountsToConnect' | 'relinkAccountsToLatestConsent',
  bic: string,
  ref: string | undefined,
  countryCode: string | undefined,
  failureMessage: string,
  // FIN-1567: only getBankAccountsToConnect sends this -- relinkAccountsToLatestConsent renews
  // whatever type the account's existing consent already carries, never a client-chosen one.
  accountType?: AccountType,
): Promise<T> {
  try {
    const { data, error } = await supabase.functions.invoke('account-api', {
      body: {
        action,
        bic,
        ...bankIdentityFields(ref, countryCode, accountType),
      },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });

    throwIfCoded(error);

    return data;
  } catch (error) {
    const functionError = await extractFunctionError(error);
    rethrowIfConsentOrKycRequired(error, functionError, { includeKyc: true });

    await handleError(error, failureMessage, action, functionError);
    throw error;
  }
}

/**
 * API client service for account-related operations
 */
export class AccountApiClient {

  /**
   * Get available bank accounts to connect for a specific BIC
   * @param bic Bank BIC code
   * @returns List of available bank accounts to connect or throws CONSENT_REQUIRED error
   */
  static async getBankAccountsToConnect(bic: string, ref?: string, countryCode?: string, accountType?: AccountType): Promise<{
    accounts: BankAccountSummary[]
  }> {
    return invokeBankIdentityAction('getBankAccountsToConnect', bic, ref, countryCode, translateOutsideReact('consultantErrors.fallback.failedToFetchAvailableBankAccounts'), accountType);
  }

  /**
   * Get bank accounts for the current user
   * @param bic Optional BIC to get accounts for specific bank with consent checking
   * @returns List of bank accounts or throws CONSENT_REQUIRED error
   */
  static async getBankAccounts(bic?: string): Promise<{ 
    accounts: BankAccountSummary[] 
  }> {
    try {
      const { data, error } = await supabase.functions.invoke('account-api', {
        body: {
          action: 'getBankAccounts',
          ...(bic && { bic })
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      
      throwIfCoded(error);

      return data;
    } catch (error) {
      const functionError = await extractFunctionError(error);
      rethrowIfConsentOrKycRequired(error, functionError);

      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchBankAccounts'), 'getBankAccounts', functionError);
      throw error;
    }
  }
  
  /**
   * Connect a bank account
   * @param accountId Account ID
   * @param bic Bank BIC
   * @returns Connection result with account details
   */
  static async connectAccount(accountId: string, bic: string, ref?: string, countryCode?: string, accountType?: AccountType): Promise<ConnectAccountResult> {
    try {
      const { data, error } = await supabase.functions.invoke('account-api', {
        body: {
          action: 'connectAccount',
          accountId,
          bic,
          ...bankIdentityFields(ref, countryCode, accountType),
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });

      throwIfCoded(error);

      toast.success(translateOutsideReact('consultantToasts.api.bankAccountConnected'));
      return data;
    } catch (error) {
      const functionError = await extractFunctionError(error);
      rethrowIfConsentOrKycRequired(error, functionError);

      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToConnectBankAccount'), 'connectAccount', functionError);
      throw error;
    }
  }

  /**
   * After "Update access" (FIN-863): move this bank's connected accounts onto its newest consent.
   */
  static async relinkAccountsToLatestConsent(bic: string, ref?: string, countryCode?: string): Promise<RelinkAccountsResult> {
    return invokeBankIdentityAction('relinkAccountsToLatestConsent', bic, ref, countryCode, translateOutsideReact('consultantErrors.fallback.failedToUpdateBankAccess'));
  }

  /**
   * Refresh consent for a bank account
   * @param consentId Consent ID
   * @returns Updated consent
   */
  static async refreshConsent(consentId: string): Promise<Consent> {
    try {
      const { data, error } = await supabase.functions.invoke('account-api', {
        body: {
          action: 'refreshConsent',
          consentId
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });

      throwIfCoded(error);

      toast.success(translateOutsideReact('consultantToasts.api.bankAccountAccessRefreshed'));
      return data;
    } catch (error) {
      const functionError = await extractFunctionError(error);
      rethrowIfConsentOrKycRequired(error, functionError);

      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToRefreshBankAccountAccess'), 'refreshConsent', functionError);
      throw error;
    }
  }

  /**
   * Multi Bank Account (FIN-90) PR4, F4/AC13: change a bank account's kontoplan mapping.
   * `CHART_MAPPING_LOCKED` (F5/AC14) and `CHART_ACCOUNT_UNAVAILABLE` (AC23's swap is the remedy)
   * are designed outcomes, not toast-worthy failures -- re-thrown with their `error` code
   * intact, same "expected outcome, not a failure" treatment CONSENT_REQUIRED gets above. So
   * are FIN-968's CHART_ACCOUNT_HAS_HISTORY (the cell asks the consultant to confirm, then
   * retries with `adoptLedgerHistory`), CHART_ACCOUNT_OUT_OF_RANGE and
   * CHART_MAPPING_PRIMARY_SWAP_ONLY.
   */
  static async changeChartMapping(
    bankAccountId: string,
    newAccountNumber: string,
    adoptLedgerHistory = false,
  ): Promise<{ chartAccountNumber: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('account-api', {
        body: { action: 'changeChartMapping', bankAccountId, newAccountNumber, adoptLedgerHistory },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });

      if (error) throw error;
      return data;
    } catch (error) {
      const functionError = await rethrowDesignedMappingOutcome(error, CHANGE_MAPPING_OUTCOMES);
      await handleError(error, translateOutsideReact('consultantErrors.fallback.couldntChangeTheBankAccountMapping'), 'changeChartMapping', functionError);
      throw error;
    }
  }

  /**
   * AC23/R-SWAP-1: swap two of this company's accounts' mapped numbers atomically.
   * `expectedNumberA/B` are the numbers the caller showed for each account; the server refuses
   * with CHART_MAPPING_CHANGED once either has moved (FIN-968). That, CHART_ACCOUNT_UNAVAILABLE
   * (a concurrent change took a number first, G4/G12) and CHART_ACCOUNT_OUT_OF_RANGE are
   * re-thrown coded so the cell can name the reason.
   */
  static async swapChartMappings(
    bankAccountIdA: string,
    bankAccountIdB: string,
    expectedNumberA: string,
    expectedNumberB: string,
  ): Promise<
    { swapped: true } | { swapped: false; lockedAccountId: string; journalEntryItemCount: number }
  > {
    try {
      const { data, error } = await supabase.functions.invoke('account-api', {
        body: { action: 'swapChartMappings', bankAccountIdA, bankAccountIdB, expectedNumberA, expectedNumberB },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });

      if (error) throw error;
      return data;
    } catch (error) {
      const functionError = await rethrowDesignedMappingOutcome(error, SWAP_MAPPING_OUTCOMES);
      await handleError(error, translateOutsideReact('consultantErrors.fallback.couldntSwapTheBankAccountMappings'), 'swapChartMappings', functionError);
      throw error;
    }
  }

  /**
   * Get the total bank balance in the specified currency
   * @param targetCurrency The currency to convert all balances to (default: 'USD')
   * @returns Object with balance and currency
   */
  static async getBankBalance(targetCurrency: string = 'USD'): Promise<{ balance: number, currency: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('account-api', {
        body: {
          action: 'getBankBalance',
          currency: targetCurrency,
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      throwIfCoded(error);

      return data;
    } catch (error) {
      const functionError = await extractFunctionError(error);
      rethrowIfConsentOrKycRequired(error, functionError);

      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchBankBalance'), 'getBankBalance', functionError);
      throw error;
    }
  }

  /** FIN-1021: every settlement waiting on a bank account mapping, for the /bank page banner
   * and transaction-row labels (AC1-AC13). Never falls back to an empty summary on failure —
   * the caller (useWaitingSettlements) renders EC7's "Couldn't check" state instead. */
  static async getWaitingSettlements(): Promise<WaitingSettlementsSummary> {
    const { data, error } = await supabase.functions.invoke('account-api', {
      body: { action: 'getWaitingSettlements' },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });
    if (error) throw error;
    return data;
  }

  /**
   * FIN-1021/AC6: "Retry booking" for a mapped account whose entries never got sent for
   * booking. `succeeded: false` means the retry itself errored server-side (not a thrown
   * error) -- the caller must check it rather than assuming a 200 means the retry worked
   * (`.claude/rules/silent-incomplete-success.md`).
   */
  static async retryBooking(bankAccountId: string): Promise<{ succeeded: boolean }> {
    try {
      const { data, error } = await supabase.functions.invoke('account-api', {
        body: { action: 'retryBooking', bankAccountId },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      if (error) throw error;
      return data;
    } catch (error) {
      const functionError = await extractFunctionError(error);
      await handleError(error, translateOutsideReact('consultantErrors.fallback.couldntRetryBooking'), 'retryBooking', functionError);
      throw error;
    }
  }
}
